import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router'

import { COMPACT_NAV_QUERY } from './components/useIsMobile'
import { NotFoundPage } from './features/notFound/NotFoundPage'
import { Layout } from './Layout'
import { parseSetupStep } from './lib/firstRun'
import { BOTTOM_NAV_ORDER, isSettingsGroup, LIBRARY_REDIRECTS, navDirection, parseLibraryTab, RAIL_ORDER } from './lib/navigation'

/**
 * Every page and redirect (redesign.md §3, §8). The signed-in shell is a pathless layout; guided setup sits outside
 * it, full page. Each page is its own chunk: the chart library and the shared Kotlin maths load only with the pages
 * that use them. The routes are written out one by one because TanStack Router types each path from its literal.
 */
const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: () => <Layout><NotFoundPage /></Layout> })

const appRoute = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: Layout })

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/today' })
  },
})

// Guided setup ---------------------------------------------------------------------------------------------------

const setupIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup',
  beforeLoad: () => {
    throw redirect({ to: '/setup/$step', params: { step: '0' } })
  },
})

const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup/$step',
  beforeLoad: ({ params }) => {
    if (parseSetupStep(params.step) === null) throw redirect({ to: '/setup/$step', params: { step: '0' } })
  },
  component: lazyRouteComponent(() => import('./features/setup/SetupPage'), 'SetupPage'),
})

// Today ----------------------------------------------------------------------------------------------------------

const todayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/today',
  component: lazyRouteComponent(() => import('./features/dashboard/DashboardPage'), 'DashboardPage'),
})

/** The old URL (redesign.md §5: Dashboard → Today), kept so bookmarks and emails still work. */
const dashboardRedirectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  beforeLoad: () => {
    throw redirect({ to: '/today', replace: true })
  },
})

// Diary ------------------------------------------------------------------------------------------------------------

const diaryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/diary',
  // `add`: the mobile FAB (Layout.tsx, owner decision §12 #4) navigates here to open the same Add food flow from
  // any tab, the way the phone's "+" opens its sheet over whichever tab is showing. Optional, so every other link
  // to `/diary` is unaffected.
  validateSearch: (search: Record<string, unknown>): { add?: true } => (search.add === true ? { add: true } : {}),
  component: lazyRouteComponent(() => import('./features/log/FoodLogPage'), 'FoodLogPage'),
})

/** The old URL (redesign.md §5: Food log → Diary), kept so bookmarks and emails still work. */
const logRedirectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/log',
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/diary', search: search as { add?: true }, replace: true })
  },
})

// Plan -------------------------------------------------------------------------------------------------------------

const planRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/plan',
  component: lazyRouteComponent(() => import('./features/strategy/StrategyPage'), 'StrategyPage'),
})

/** The old URL (redesign.md §5: Strategy → Plan), kept so bookmarks and emails still work. */
const strategyRedirectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/strategy',
  beforeLoad: () => {
    throw redirect({ to: '/plan', replace: true })
  },
})

const planReviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/plan/review',
  component: lazyRouteComponent(() => import('./features/checkin/CheckInPage'), 'CheckInPage'),
})

/** The old URL (redesign.md §5: Check-in → Weekly review), kept so bookmarks and emails still work. */
const checkInRedirectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/check-in',
  beforeLoad: () => {
    throw redirect({ to: '/plan/review', replace: true })
  },
})

// Progress -------------------------------------------------------------------------------------------------------

/** The Progress hub (redesign.md §3): weight trend, energy expenditure, nutrients, body metrics, progress photos,
 * history and consistency are reached from here; each keeps its own existing URL below so nothing that already
 * bookmarks them breaks. */
const progressRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/progress',
  component: lazyRouteComponent(() => import('./features/progress/ProgressPage'), 'ProgressPage'),
})

const consistencyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/progress/consistency',
  component: lazyRouteComponent(() => import('./features/progress/ConsistencyPage'), 'ConsistencyPage'),
})

const weightRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/weight',
  component: lazyRouteComponent(() => import('./features/weight/WeightPage'), 'WeightPage'),
})

const expenditureRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/expenditure',
  component: lazyRouteComponent(() => import('./features/expenditure/ExpenditurePage'), 'ExpenditurePage'),
})

const nutrientsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/nutrients',
  component: lazyRouteComponent(() => import('./features/nutrients/NutrientsPage'), 'NutrientsPage'),
})

const photosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/photos',
  component: lazyRouteComponent(() => import('./features/photos/PhotosPage'), 'PhotosPage'),
})

const historyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/history',
  component: lazyRouteComponent(() => import('./features/history/HistoryPage'), 'HistoryPage'),
})

// Food (reached from inside Diary, `DiaryPage`'s Library link) -----------------------------------------------------

const libraryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/library',
  validateSearch: (search: Record<string, unknown>) => ({ tab: parseLibraryTab(search.tab) }),
  component: lazyRouteComponent(() => import('./features/library/LibraryPage'), 'LibraryPage'),
})

const recipeEditorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/library/recipes/$id',
  component: lazyRouteComponent(() => import('./features/library/RecipeEditorPage'), 'RecipeEditorPage'),
})

const mealEditorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/library/meals/$id',
  component: lazyRouteComponent(() => import('./features/library/MealEditorPage'), 'MealEditorPage'),
})

/** An old page that moved into a Library tab; generic over the path so the route keeps its literal type. */
function libraryRedirect<const P extends keyof typeof LIBRARY_REDIRECTS>(path: P) {
  return createRoute({
    getParentRoute: () => appRoute,
    path,
    beforeLoad: () => {
      throw redirect({ to: '/library', search: { tab: LIBRARY_REDIRECTS[path] }, replace: true })
    },
  })
}

const libraryRedirectRoutes = [libraryRedirect('/foods'), libraryRedirect('/custom-foods'), libraryRedirect('/recipes'), libraryRedirect('/meals')] as const

// Train ----------------------------------------------------------------------------------------------------------

/** The Train hub (redesign.md §3): workouts, programs and equipment are reached from here, each at its own existing
 * URL below (unchanged, so nothing that already bookmarks them breaks); there is nothing here that logs a workout
 * (redesign.md §8: web workout logging is for a later redesign, not this one). */
const trainRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/train',
  component: lazyRouteComponent(() => import('./features/train/TrainPage'), 'TrainPage'),
})

const workoutsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/workouts',
  component: lazyRouteComponent(() => import('./features/training/WorkoutsPage'), 'WorkoutsPage'),
})

const programsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/programs',
  component: lazyRouteComponent(() => import('./features/training/ProgramsPage'), 'ProgramsPage'),
})

const programEditorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/programs/$id',
  component: lazyRouteComponent(() => import('./features/training/ProgramEditorPage'), 'ProgramEditorPage'),
})

const gymsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/gyms',
  component: lazyRouteComponent(() => import('./features/training/GymsPage'), 'GymsPage'),
})

const gymEditorRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/gyms/$id',
  component: lazyRouteComponent(() => import('./features/training/GymEditorPage'), 'GymEditorPage'),
})

// You (settings, account, about, export; the top-right button on every page, never a tab) --------------------------

const youRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/you',
  component: lazyRouteComponent(() => import('./features/you/YouPage'), 'YouPage'),
})

/** The old URL (the bottom nav's "More" catch-all, replaced by the "You" button everywhere, redesign.md §3). */
const moreRedirectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/more',
  beforeLoad: () => {
    throw redirect({ to: '/you', replace: true })
  },
})

// `/export/download`: the one link the app ever shows or shares for an export (Settings, the suspended page,
// "Download again"); allowed for a suspended account too (SuspendedGate.tsx checks this exact pathname).
const exportDownloadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/export/download',
  component: lazyRouteComponent(() => import('./features/account/ExportDownloadPage'), 'ExportDownloadPage'),
})

// Settings -------------------------------------------------------------------------------------------------------

const settingsIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/$group', params: { group: 'profile' }, replace: true })
  },
})

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/settings/$group',
  beforeLoad: ({ params }) => {
    if (!isSettingsGroup(params.group)) throw redirect({ to: '/settings/$group', params: { group: 'profile' }, replace: true })
  },
  component: lazyRouteComponent(() => import('./features/settings/SettingsPage'), 'SettingsPage'),
})

const aboutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/about',
  component: lazyRouteComponent(() => import('./features/about/AboutPage'), 'AboutPage'),
})

export const routeTree = rootRoute.addChildren([
  indexRoute,
  setupIndexRoute,
  setupRoute,
  appRoute.addChildren([
    todayRoute,
    dashboardRedirectRoute,
    diaryRoute,
    logRedirectRoute,
    planRoute,
    strategyRedirectRoute,
    planReviewRoute,
    checkInRedirectRoute,
    progressRoute,
    consistencyRoute,
    weightRoute,
    expenditureRoute,
    nutrientsRoute,
    photosRoute,
    historyRoute,
    libraryRoute,
    recipeEditorRoute,
    mealEditorRoute,
    ...libraryRedirectRoutes,
    trainRoute,
    workoutsRoute,
    programsRoute,
    programEditorRoute,
    gymsRoute,
    gymEditorRoute,
    youRoute,
    moreRedirectRoute,
    exportDownloadRoute,
    settingsIndexRoute,
    settingsRoute,
    aboutRoute,
  ]),
])

/**
 * Directional slide on every route change (owner override, 24 Oct 2026, replacing an earlier fade-through plan):
 * `document.startViewTransition({update, types})` carries `navDirection`'s result as a view-transition type, which
 * styles.css's `:active-view-transition-type()` rules key off to slide the right way. `window.matchMedia` rather
 * than `useCompactNav` because this runs inside the router, outside React. Browsers without view-transition types
 * (or without the API at all) just keep the plain swap there was before this — a safe fallback, never a regression.
 */
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultViewTransition: {
    types: ({ fromLocation, toLocation, pathChanged }) => {
      if (!pathChanged || !fromLocation) return false
      const compact = window.matchMedia(COMPACT_NAV_QUERY).matches
      const direction = navDirection(compact ? BOTTOM_NAV_ORDER : RAIL_ORDER, fromLocation.pathname, toLocation.pathname)
      return direction === 'none' ? false : [direction]
    },
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
